import { v4 as uuidv4 } from 'uuid';

import { IRemoteAgentSiteSettings } from '~src/settings/types/ISiteAdminBridgeSettings';

import { ISiteSettingsForServerAdmin } from '../../../settings/types/ISite';
import { ISiteCommentingSettings } from '../../../settings/types/ISiteCommentingSettings';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin, ISiteSubscriptionSettings } from '../../../settings/types/ISiteSettingsForSiteAdmin';
import { SiteSettingServerAdminTelemetryEvent, SiteSettingSiteAdminTelemetryEvent, TelemetryEvents } from '../TelemetryEvents';

export interface SettingUUID {
    uuid: string | undefined;
    isSiteAdmin: boolean | undefined;
}

let siteSettingForSiteAdminUUID: SettingUUID  = {uuid: undefined, isSiteAdmin: undefined};

export const buildSiteSettingServerAdminTelemetryEvent = (
    site: ISiteSettingsForServerAdmin, master: ISiteSettingsForServerAdmin,eventId: string): SiteSettingServerAdminTelemetryEvent => ({
        type: TelemetryEvents.SiteSettingServerAdmin,
        adminCount_new: site.adminCount,
        adminCount_old: master.adminCount,
        authoringEnabled_new: site.settings.authoringEnabled,
        authoringEnabled_old: master.settings.authoringEnabled,
        availability_new: site.availability,
        availability_old: master.availability,
        cacheWarmupEnabled_new: site.settings.cacheWarmupEnabled,
        cacheWarmupEnabled_old: master.settings.cacheWarmupEnabled,
        catalogingOnSiteEnabled_new:site.settings.catalogingOnSiteEnabled,
        catalogingOnSiteEnabled_old: master.settings.catalogingOnSiteEnabled,
        contentVersionLimit_new: site.settings.contentVersionLimit,
        contentVersionLimit_old: master.settings.contentVersionLimit,
        contentVersionLimitEnabled_new: site.settings.contentVersionLimitEnabled,
        contentVersionLimitEnabled_old:master.settings.contentVersionLimitEnabled,
        currentLoggedInUserCount_new: site.currentLoggedInUserCount,
        currentLoggedInUserCount_old: master.currentLoggedInUserCount,
        customSubscriptionEmailEnabled_new: site.settings.customSubscriptionEmailEnabled,
        customSubscriptionEmailEnabled_old: master.settings.customSubscriptionEmailEnabled,
        customSubscriptionFooterEnabled_new: site.settings.customSubscriptionFooterEnabled,
        customSubscriptionFooterEnabled_old: master.settings.customSubscriptionFooterEnabled,
        flowsOnSiteEnabled_new: site.settings.flowsOnSiteEnabled,
        flowsOnSiteEnabled_old: master.settings.flowsOnSiteEnabled,
        guestAccessEnabled_new: site.settings.guestAccessEnabled,
        guestAccessEnabled_old: master.settings.guestAccessEnabled,
        licensedUserCount_new: site.licensedUserCount,
        licensedUserCount_old:master.licensedUserCount,
        metricsEnabled_new: site.settings.metricsEnabled,
        metricsEnabled_old: master.settings.metricsEnabled,
        siteAdminsCanAddUsers_new: site.settings.siteAdminsCanAddUsers,
        siteAdminsCanAddUsers_old: master.settings.siteAdminsCanAddUsers,
        storageQuotaBytes_new: site.settings.storageQuotaBytes,
        storageQuotaBytes_old: master.settings.storageQuotaBytes,
        storageQuotaEnabled_new:site.settings.storageQuotaEnabled,
        storageQuotaEnabled_old: master.settings.storageQuotaEnabled,
        storageQuotaPercentUsed_new: site.storageQuotaPercentUsed,
        storageQuotaPercentUsed_old: master.storageQuotaPercentUsed,
        storageUsedBytes_new: site.storageUsedBytes,
        storageUsedBytes_old: master.storageUsedBytes,
        userCount_new: site.userCount,
        userCount_old: master.userCount,
        userQuota_new: site.settings.userQuota,
        userQuota_old: master.settings.userQuota,
        userQuotaMode_new: site.settings.userQuotaMode,
        userQuotaMode_old: master.settings.userQuotaMode,
        versionHistoryEnabled_new:site.settings.versionHistoryEnabled,
        versionHistoryEnabled_old:master.settings.versionHistoryEnabled,
        runNowEnabled_new: site.settings.runNowEnabled,
        runNowEnabled_old: master.settings.runNowEnabled,
        eventid: eventId,
        siteId: site.id,
        tierCreatorCapacity_new: site.settings.tierCreatorCapacity,
        tierCreatorCapacity_old: master.settings.tierCreatorCapacity,
        tierExplorerCapacity_new: site.settings.tierExplorerCapacity,
        tierExplorerCapacity_old: master.settings.tierExplorerCapacity,
        tierViewerCapacity_new: site.settings.tierViewerCapacity,
        tierViewerCapacity_old: master.settings.tierViewerCapacity,
        creatorCount_new: site.creatorCount,
        creatorCount_old: master.creatorCount,
        explorerCount_new: site.explorerCount,
        explorerCount_old: master.explorerCount,
        viewerCount_new: site.viewerCount,
        viewerCount_old: master.viewerCount
});

const buildNewSubscriptionSettings = (newSubscriptionSettings: ISiteSubscriptionSettings): {} => {
    if(newSubscriptionSettings) {
        return {
            allow_subscription_attachments_new: newSubscriptionSettings.allowSubscriptionAttachments,
            custom_schedule_subscription_for_site_enabled_new: newSubscriptionSettings.customScheduleSubscriptionForSiteEnabled,
            server_allow_subscription_attachments_new: newSubscriptionSettings.serverAllowSubscriptionAttachments,
            subscribe_others_enabled_new: newSubscriptionSettings.subscribeOthersEnabled,
            subscriptions_enabled_new : newSubscriptionSettings.subscriptionsEnabled,
        };
    }
    return {};
};

const builOldSubscriptionSettings = (oldSubscriptionSettings: ISiteSubscriptionSettings): {} => {
    if(oldSubscriptionSettings) {
        return {
            allow_subscription_attachments_old: oldSubscriptionSettings.allowSubscriptionAttachments,
            custom_schedule_subscription_for_site_enabled_old: oldSubscriptionSettings.customScheduleSubscriptionForSiteEnabled,
            server_allow_subscription_attachments_old: oldSubscriptionSettings.serverAllowSubscriptionAttachments,
            subscribe_others_enabled_old: oldSubscriptionSettings.subscribeOthersEnabled,
            subscriptions_enabled_old : oldSubscriptionSettings.subscriptionsEnabled,
        };
    }
    return {};
};

const buildNewSiteCommentingSettings = (newSiteCommentingSettings: ISiteCommentingSettings): {} => {
    if(newSiteCommentingSettings) {
        return {
            commenting_enabled_new: newSiteCommentingSettings.commentingEnabled,
            commenting_mentions_enabled_new: newSiteCommentingSettings.commentingMentionsEnabled,
        };
    }
    return {};
};

const buildOldSiteCommentingSettings = (oldSiteCommentingSettings: ISiteCommentingSettings): {} => {
    if(oldSiteCommentingSettings) {
        return {
            commenting_enabled_old: oldSiteCommentingSettings.commentingEnabled,
            commenting_mentions_enabled_old: oldSiteCommentingSettings.commentingMentionsEnabled,
        };
    }
    return {};
};

const buildNewNotificationForClientNotRunningEnabled = (newRemoteAgentSettings: IRemoteAgentSiteSettings): {} => {
    if(newRemoteAgentSettings) {
        return {
            notification_for_client_not_running_enabled_new: newRemoteAgentSettings.notificationForClientNotRunningEnabled,
        };
    }
    return {};
};

const buildOldNotificationForClientNotRunningEnabled = (oldRemoteAgentSettings: IRemoteAgentSiteSettings): {} => {
    if(oldRemoteAgentSettings) {
        return {
            notification_for_client_not_running_enabled_old: oldRemoteAgentSettings.notificationForClientNotRunningEnabled,
        };
    }
    return {};
};

export const buildSiteSettingSiteAdminTelemetryEvent = (
    site: ISiteSettingsForSiteAdmin, master: ISiteSettingsForSiteAdmin, eventId: string): SiteSettingSiteAdminTelemetryEvent => {
        // remoteAgentSettings and siteCommentingSettings could be undefined inside ISiteAdminSettings
        // make sure return undefined when accessing properties inside remoteAgentSettings and siteCommentingSettings when they are undefined

        const newSiteAdminSettings = site.siteAdminSettings as ISiteAdminSettings;
        const oldSiteAdminSettings = master.siteAdminSettings as ISiteAdminSettings;

        return {
            ...buildNewSubscriptionSettings(site.subscriptionSettings!),
            ...builOldSubscriptionSettings(master.subscriptionSettings!),
            ...buildNewSiteCommentingSettings(newSiteAdminSettings.siteCommentingSettings),
            ...buildOldSiteCommentingSettings(oldSiteAdminSettings.siteCommentingSettings),
            ...buildNewNotificationForClientNotRunningEnabled(newSiteAdminSettings.remoteAgentSettings),
            ...buildOldNotificationForClientNotRunningEnabled(oldSiteAdminSettings.remoteAgentSettings),
            type: TelemetryEvents.SiteSettingSiteAdmin,
            add_support_user_enabled_new: newSiteAdminSettings.addSupportUserEnabled,
            add_support_user_enabled_old: oldSiteAdminSettings.addSupportUserEnabled,
            admin_insights_publish_frequency_new: newSiteAdminSettings.adminInsightsPublishFrequency,
            admin_insights_publish_frequency_old: oldSiteAdminSettings.adminInsightsPublishFrequency,
            alternative_federation_engine_enabled_new: newSiteAdminSettings.alternativeFederationEngineEnabled,
            alternative_federation_engine_enabled_old: oldSiteAdminSettings.alternativeFederationEngineEnabled,
            collections_enabled_new: newSiteAdminSettings.collectionsEnabled,
            collections_enabled_old: oldSiteAdminSettings.collectionsEnabled,
            data_alerts_enabled_new: newSiteAdminSettings.dataAlertsEnabled,
            data_alerts_enabled_old: oldSiteAdminSettings.dataAlertsEnabled,
            derived_permissions_enabled_new: newSiteAdminSettings.derivedPermissionsEnabled,
            derived_permissions_enabled_old: oldSiteAdminSettings.derivedPermissionsEnabled,
            data_orientation_enabled_new: newSiteAdminSettings.dataOrientationEnabled,
            data_orientation_enabled_old: oldSiteAdminSettings.dataOrientationEnabled,
            dashboard_narrative_enabled_new: newSiteAdminSettings.dashboardNarrativeEnabled,
            dashboard_narrative_enabled_old: oldSiteAdminSettings.dashboardNarrativeEnabled,
            dashboard_qa_enabled_new: newSiteAdminSettings.siteSettingsV2?.dashboardQAEnabled ?? false,
            dashboard_qa_enabled_old: oldSiteAdminSettings.siteSettingsV2?.dashboardQAEnabled ?? false,
            extract_encryption_mode_new: newSiteAdminSettings.extractEncryptionMode,
            extract_encryption_mode_old: oldSiteAdminSettings.extractEncryptionMode,
            generative_ai_web_authoring_new: newSiteAdminSettings.generativeAiWebAuthoring,
            generative_ai_web_authoring_old: oldSiteAdminSettings.generativeAiWebAuthoring,
            generative_ai_web_authoring_on_prem_enabled_new: newSiteAdminSettings.generativeAiWebAuthoringOnPremEnabled,
            generative_ai_web_authoring_on_prem_enabled_old: oldSiteAdminSettings.generativeAiWebAuthoringOnPremEnabled,
            is_admin_data_source_enabled_new: newSiteAdminSettings.isAdminDataSourceEnabled,
            is_admin_data_source_enabled_old: oldSiteAdminSettings.isAdminDataSourceEnabled,
            named_sharing_enabled_new: newSiteAdminSettings.siteNamedSharingSettings.namedSharingEnabled,
            named_sharing_enabled_old: oldSiteAdminSettings.siteNamedSharingSettings.namedSharingEnabled,
            notification_enabled_new: newSiteAdminSettings.notificationEnabled,
            notification_enabled_old: oldSiteAdminSettings.notificationEnabled,
            personal_space_enabled_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceEnabled,
            personal_space_enabled_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceEnabled,
            personal_space_storage_quota_set_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuotaSet,
            personal_space_storage_quota_set_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuotaSet,
            personal_space_storage_quota_new: newSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuota,
            personal_space_storage_quota_old: oldSiteAdminSettings.sitePersonalSpaceSettings.personalSpaceStorageQuota,
            refresh_token_enabled_new: newSiteAdminSettings.refreshTokenEnabled,
            refresh_token_enabled_old: oldSiteAdminSettings.refreshTokenEnabled,
            remote_querying_enabled_new: newSiteAdminSettings.remoteQueryingEnabled,
            remote_querying_enabled_old: oldSiteAdminSettings.remoteQueryingEnabled,
            request_access_enabled_new: newSiteAdminSettings.requestAccessEnabled,
            request_access_enabled_old: oldSiteAdminSettings.requestAccessEnabled,
            sheet_image_enabled_new: newSiteAdminSettings.sheetImageEnabled,
            sheet_image_enabled_old: oldSiteAdminSettings.sheetImageEnabled,
            site_invite_email_enabled_new: newSiteAdminSettings.siteInviteEmailEnabled,
            site_invite_email_enabled_old: oldSiteAdminSettings.siteInviteEmailEnabled,
            user_visibility_new: newSiteAdminSettings.userVisibility,
            user_visibility_old: oldSiteAdminSettings.userVisibility,
            generative_ai_enabled_new: newSiteAdminSettings.generativeAiEnabled,
            generative_ai_enabled_old: oldSiteAdminSettings.generativeAiEnabled,
            viz_recommendations_enabled_new: newSiteAdminSettings.vizRecommendationsEnabled,
            viz_recommendations_enabled_old: oldSiteAdminSettings.vizRecommendationsEnabled,
            viz_rec_username_visibility_enabled_new: newSiteAdminSettings.vizRecUsernameVisibilityEnabled,
            viz_rec_username_visibility_enabled_old: oldSiteAdminSettings.vizRecUsernameVisibilityEnabled,
            pulse_enabled_new: newSiteAdminSettings.pulseEnabled,
            pulse_enabled_old: oldSiteAdminSettings.pulseEnabled,
            pulse_group_id_new: newSiteAdminSettings.pulseGroupId,
            pulse_group_id_old: oldSiteAdminSettings.pulseGroupId,
            eventid: eventId,
            siteId: site.siteId,
            flow_federation_strategy_selected_connection_enabled_new: newSiteAdminSettings.flowFederationStrategySelectedConnectionEnabled,
            flow_federation_strategy_selected_connection_enabled_old: oldSiteAdminSettings.flowFederationStrategySelectedConnectionEnabled,
    };
};

// this method is used by buildSiteSettingSiteAdminTelemetryEvent and buildSiteSettingServerAdminTelemetryEvent
// need to make sure they have the same UUID when save button is cliced
export const getSiteSettingUUID = (isSiteAdmin: boolean): string => {
    // for online site admin setting change will only call updateSiteSettingsForSiteAdmin
    // we should only share uuid if one save action trigger both updateSiteSettingsForSiteAdmin  and
    // updateSiteSettingsForServerAdmin
    if(siteSettingForSiteAdminUUID.uuid && siteSettingForSiteAdminUUID.isSiteAdmin !== isSiteAdmin) {
        const uuid = siteSettingForSiteAdminUUID.uuid;
        siteSettingForSiteAdminUUID = {uuid: undefined, isSiteAdmin: undefined};
        return uuid;
    }

    siteSettingForSiteAdminUUID = {uuid: uuidv4(), isSiteAdmin: isSiteAdmin};
    return siteSettingForSiteAdminUUID.uuid;
};
